import { tierFor } from '@ce/core';
import { badgeStyle, btn, divider, kicker, settle, t } from '../styles';
import { CardArc } from '../components/Chrome';
import { PulseArc, Sheen } from '../components/Ambient';
import type { ScreenProps } from './types';

export function Quests({ bundle, state, dispatch }: ScreenProps) {
  const quests = bundle.quests;
  const { current, next, pct } = tierFor(bundle, state.points);
  const question = quests.quiz[state.quizIdx];
  const revealed = state.quizPick !== null;
  const lastQuestion = state.quizIdx === quests.quiz.length - 1;

  return (
    <div className="stagger">
      <div
        style={{
          padding: 16, borderRadius: 12,
          background: 'linear-gradient(140deg, var(--color-accent-900), var(--color-surface) 72%)',
          boxShadow: 'var(--shadow-sm)', position: 'relative', overflow: 'hidden',
        }}
      >
        <Sheen delay={2.4} />
        <CardArc size={210} top={-80} right={-70} />
        <CardArc size={130} top={-40} right={-30} opacity={0.12} />
        <PulseArc size={130} top={-40} right={-30} opacity={0.28} delay={1.1} duration={7} />
        <div style={kicker(9.5, 'var(--color-accent-300)')}>Badge {bundle.badge.code}</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, margin: '11px 0 4px' }}>
          <div style={{ font: '500 34px/1 var(--font-body)', letterSpacing: '-0.03em' }}>
            {state.points}
          </div>
          <div style={{ font: '500 13px/1 var(--font-body)', color: t(0.6) }}>
            points · {current.n}
          </div>
        </div>
        <div
          style={{
            height: 6, borderRadius: 3, background: 'rgba(var(--color-text-rgb), 0.1)',
            margin: '14px 0 8px', overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%', width: `${pct}%`, borderRadius: 3,
              background: 'linear-gradient(to right, var(--color-accent-700), var(--color-accent-400))',
              transition: 'width .4s ease',
            }}
          />
        </div>
        <div style={{ fontSize: 11.5, color: t(0.55) }}>
          {next ? `${next.at - state.points} points to ${next.n}` : 'Top tier reached'}
        </div>
      </div>

      <div style={{ ...kicker(), margin: '24px 0 11px' }}>Daily quiz</div>
      {!state.quizDone && question ? (
        <div
          style={{
            padding: 16, borderRadius: 10, background: 'var(--color-surface)',
            boxShadow: 'var(--shadow-sm)',
          }}
        >
          <div style={{ font: '400 10.5px/1 var(--font-mono)', color: 'var(--color-accent-300)' }}>
            Question {state.quizIdx + 1} of {quests.quiz.length}
          </div>
          <div
            style={{
              font: '500 16px/1.35 var(--font-body)', margin: '10px 0 14px', textWrap: 'pretty',
            }}
          >
            {question.q}
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            {question.a.map((label, i) => {
              const picked = state.quizPick === i;
              const right = i === question.correct;
              const border = revealed && right ? 'var(--color-accent)'
                : picked ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.16)';
              const bg = revealed && right ? 'rgba(var(--color-accent-rgb), 0.2)'
                : picked ? 'rgba(var(--color-accent-rgb), 0.1)' : 'transparent';
              return (
                <button
                  key={label}
                  type="button"
                  disabled={revealed}
                  onClick={() =>
                    dispatch({ type: 'quizPick', index: i, points: question.points, correct: right })
                  }
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px',
                    borderRadius: 9, font: '500 13.5px/1.3 var(--font-body)',
                    cursor: revealed ? 'default' : 'pointer',
                    border: `1px solid ${border}`, background: bg, color: 'var(--color-text)',
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
          {revealed && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
              <div style={{ flex: 1, fontSize: 12.5, color: t(0.65) }}>
                {state.quizPick === question.correct
                  ? `+${question.points} points — correct.`
                  : `Not quite. The answer is ${question.a[question.correct]}.`}
              </div>
              <button
                type="button"
                onClick={() => dispatch({ type: 'quizNext', last: lastQuestion })}
                style={btn('primary', { fontSize: 12.5 })}
              >
                {lastQuestion ? 'Finish' : 'Next question'}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div
          style={{
            padding: 16, borderRadius: 10, border: '1px solid var(--color-accent-700)',
            background: 'var(--color-accent-900)',
          }}
        >
          <div style={{ font: '500 15px/1.3 var(--font-body)' }}>Today&rsquo;s quiz is done</div>
          <div style={{ fontSize: 12.5, color: t(0.6), marginTop: 6 }}>
            {state.quizScore} of {quests.quiz.length} right · {state.quizScore * (quests.quiz[0]?.points ?? 50)} points banked
          </div>
          <button
            type="button"
            onClick={() => dispatch({ type: 'quizReset' })}
            style={btn('secondary', { fontSize: 12.5, marginTop: 12 })}
          >
            Play again
          </button>
        </div>
      )}

      <div style={{ ...kicker(), margin: '24px 0 11px' }}>Weekend challenges</div>
      <div style={{ display: 'grid', gap: 9 }}>
        {quests.challenges.map((c, i) => {
          const done = c.have >= c.need;
          return (
            <div
              key={c.id}
              style={{
                padding: '13px 14px', borderRadius: 10, background: 'var(--color-surface)',
                boxShadow: 'var(--shadow-sm)', ...settle(i),
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0, font: '500 14px/1.25 var(--font-body)' }}>
                  {c.label}
                </div>
                <div style={badgeStyle(done)}>{done ? 'Done' : 'In progress'}</div>
              </div>
              <div style={{ fontSize: 11.5, color: t(0.5), marginTop: 5 }}>{c.note}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 11 }}>
                <div
                  style={{
                    flex: 1, height: 4, borderRadius: 2,
                    background: 'rgba(var(--color-text-rgb), 0.1)', overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%', width: `${Math.round((c.have / c.need) * 100)}%`,
                      borderRadius: 2,
                      background: done ? 'var(--color-accent)' : 'var(--color-accent-700)',
                    }}
                  />
                </div>
                <div style={{ font: '400 10.5px/1 var(--font-mono)', color: t(0.5) }}>
                  {c.have}/{c.need}
                </div>
                <div style={{ font: '500 11px/1 var(--font-body)', color: 'var(--color-accent-300)' }}>
                  +{c.pts}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ ...kicker(), margin: '24px 0 11px' }}>Prize vault</div>
      <div style={{ display: 'grid', gap: 9 }}>
        {quests.prizes.map((p) => {
          const got = state.redeemed.includes(p.id);
          const can = state.points >= p.cost && !got;
          return (
            <button
              key={p.id}
              type="button"
              disabled={!can}
              onClick={() =>
                can &&
                dispatch({
                  type: 'redeem',
                  id: p.id,
                  cost: p.cost,
                  voucher: {
                    title: p.label,
                    meta: 'Prize redemption · show at the info desk',
                    code: `PRZ-${p.cost}`,
                  },
                })
              }
              style={{
                display: 'flex', alignItems: 'center', gap: 13, width: '100%', textAlign: 'left',
                padding: 14, borderRadius: 10, cursor: can ? 'pointer' : 'not-allowed',
                opacity: can || got ? 1 : 0.5,
                border: `1px solid ${got ? 'var(--color-accent)' : divider}`,
                background: got ? 'rgba(var(--color-accent-rgb), 0.12)' : 'transparent',
                color: 'var(--color-text)',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ font: '500 14px/1.25 var(--font-body)' }}>{p.label}</div>
                <div style={{ fontSize: 11.5, lineHeight: 1.45, color: t(0.5), marginTop: 4 }}>
                  {got ? 'Redeemed — claim code in Me' : p.note}
                </div>
              </div>
              <div style={{ flex: 'none', textAlign: 'right' }}>
                <div style={{ font: '500 13px/1 var(--font-body)' }}>{p.cost} pts</div>
                <div
                  style={{
                    ...kicker(11, got ? 'var(--color-accent-300)' : can ? 'var(--color-accent)' : t(0.4), 0.06),
                    marginTop: 4,
                  }}
                >
                  {got ? 'Claimed' : can ? 'Redeem' : 'Locked'}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div style={{ ...kicker(), margin: '24px 0 11px' }}>
        Badge sharing · {state.consents.length} of {quests.consents.length} on
      </div>
      <div
        style={{
          padding: 16, borderRadius: 10, background: 'var(--color-surface)',
          boxShadow: 'var(--shadow-sm)',
        }}
      >
        <div style={{ fontSize: 12.5, lineHeight: 1.6, color: t(0.6) }}>{bundle.strings.consentIntro}</div>
        <div style={{ display: 'grid', gap: 4, margin: '14px 0 0' }}>
          {quests.consents.map((c) => {
            const on = state.consents.includes(c.id);
            return (
              <button
                key={c.id}
                type="button"
                role="switch"
                aria-checked={on}
                onClick={() => dispatch({ type: 'toggleConsent', id: c.id })}
                style={{
                  display: 'flex', alignItems: 'center', gap: 13, textAlign: 'left',
                  padding: '11px 0', border: 0, background: 'transparent',
                  color: 'var(--color-text)', cursor: 'pointer', borderTop: `1px solid ${divider}`,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ font: '500 13.5px/1.3 var(--font-body)' }}>{c.label}</div>
                  <div style={{ fontSize: 11.5, color: t(0.5), marginTop: 4 }}>{c.note}</div>
                </div>
                <div
                  style={{
                    flex: 'none', width: 38, height: 22, borderRadius: 11, padding: 2,
                    display: 'flex', justifyContent: on ? 'flex-end' : 'flex-start',
                    border: `1px solid ${on ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.2)'}`,
                    background: on ? 'rgba(var(--color-accent-rgb), 0.24)' : 'transparent',
                    transition: 'all .18s ease',
                  }}
                >
                  <div
                    style={{
                      width: 16, height: 16, borderRadius: '50%',
                      background: on ? 'var(--color-accent-400)' : 'rgba(var(--color-text-rgb), 0.35)',
                    }}
                  />
                </div>
              </button>
            );
          })}
        </div>
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, paddingTop: 14,
            borderTop: `1px solid ${divider}`,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={kicker(10, t(0.42), 0.14)}>Contact on file</div>
            <div style={{ font: '400 12px/1.4 var(--font-mono)', color: t(0.75), marginTop: 6 }}>
              {bundle.badge.email}
            </div>
          </div>
          {state.leadSaved ? (
            <div style={kicker(11, 'var(--color-accent-300)', 0.06)}>Saved</div>
          ) : (
            <button
              type="button"
              onClick={() => dispatch({ type: 'saveLead' })}
              style={btn('primary', { fontSize: 12.5 })}
            >
              Save to badge
            </button>
          )}
        </div>
      </div>
      <div style={{ height: 26 }} />
    </div>
  );
}
